import { faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useState } from 'react'
import { Wrapper } from './styles'
import { IS_DESKTOP_OR_TABLET } from '..'

type Option<T extends string = string> = { value: T; label: string }

type Props<T extends string = string> = {
  value: T
  options: Option<T>[]
  onChange: (newValue: T) => void
  stacked?: boolean
  style?: React.CSSProperties
  // With two options, a click on the active one selects the other; off where every change is a confirmed save
  flipsOnActiveClick?: boolean
}

export default function MdToggle<T extends string = string>(props: Props<T>) {
  const { options, onChange } = props
  const [localValue, setLocalValue] = useState<T>(props.value)

  const isInline = !props.stacked && (IS_DESKTOP_OR_TABLET || options.length <= 3)

  const selectedValue = props.value || localValue

  const selectValue = (value: T) => {
    if (value === selectedValue && options.length === 2 && props.flipsOnActiveClick !== false) {
      toggle()
      return
    }
    setLocalValue(value)
    onChange?.(value)
  }

  const toggle = () => {
    const otherValue = options.find(x => x.value !== selectedValue)!
    setLocalValue(otherValue.value)
    onChange?.(otherValue.value)
  }

  useEffect(() => {
    const isValidOption = options.some(x => x.value === selectedValue)
    if (isValidOption) {
      return
    }
    const doesSelectedValueStillExist = options.find(x => x.value === selectedValue)
    if (!doesSelectedValueStillExist && props.options[0]) {
      selectValue(props.options[0].value)
    }
  }, [options.map(x => x.value).toString()])

  const activeIndex = options.findIndex(x => x.value === selectedValue)

  const columnsCount = isInline ? options.length : 2
  const widthPercent = 100 / columnsCount
  const translateXPercent = (activeIndex % columnsCount) * 100
  const currentRow = Math.floor(activeIndex / 2)
  const translateY = isInline ? '' : `, ${currentRow * 100}%`

  return (
    <Wrapper className={`section-toggle ${isInline ? 'inline' : ''} ${options.length === 1 ? 'single-option' : ''}`} style={props.style}>
      <div
        className="active-indicator"
        style={{
          width: `calc((100% - 4px) / ${columnsCount})`,
          transform: `translate(${translateXPercent}%${translateY})`,
        }}
      />

      {options.map(x => (
        <div
          onClick={() => selectValue(x.value)}
          key={x.value + ''}
          className={'option' + (selectedValue === x.value ? ' active' : '')}
          style={{ width: `${widthPercent}%` }}
        >
          <FontAwesomeIcon icon={faCheckCircle} />
          {IS_DESKTOP_OR_TABLET ? x.label : x.label.replace('Meniu ', '')}
        </div>
      ))}
    </Wrapper>
  )
}
